import { useApi } from '../api'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useSummaryCache } from './use-summary-cache'
import { useGlobalEvent } from '@highlight/ipc'
import { type SummaryItem } from '../types'

const SUMMARIES_QUERY_KEY = 'summaries'

export function useSummaries() {
  const { get } = useApi()
  const { invalidateSummaryQueries } = useSummaryCache()

  // Ensure summaries are updated when daily summary toast is shown
  useGlobalEvent('toast:daily-summary', () => {
    invalidateSummaryQueries()
  })

  return useQuery<SummaryItem[], Error>({
    queryKey: [SUMMARIES_QUERY_KEY],
    queryFn: async () => {
      const resp = await get('summaries', { version: 'v1' })
      if (!resp.ok) {
        throw new Error(resp.statusText ?? `Unexpected ${resp.status} error`)
      }
      const json = await resp.json()
      if (!json.success) {
        throw new Error('Unexpected error')
      }
      return json.data as SummaryItem[]
    },
    staleTime: 30 * 60 * 1000,
  })
}

export function useRegenerateSummary() {
  const { post } = useApi()
  const { invalidateSummaryQueries, invalidateAllIndividualSummaries } = useSummaryCache()

  return useMutation({
    mutationKey: ['regenerate-summary'],
    mutationFn: async () => {
      const resp = await post('summaries', undefined, { version: 'v1' })
      if (!resp.ok) {
        throw new Error(resp.statusText ?? `Unexpected ${resp.status} error`)
      }
      const json = await resp.json()
      if (!json.success) {
        throw new Error('Unexpected error')
      }
      return json.data
    },
    onSuccess: () => {
      invalidateSummaryQueries()
      invalidateAllIndividualSummaries()
    },
    onError: (err) => {
      console.error('Failed to regenerate summary:', err)
    },
  })
}

export function useMarkSummaryAsRead() {
  const { patch } = useApi()
  const { updateSummaryOptimistically, rollbackSummary, invalidateSummaryQueries } = useSummaryCache()

  return useMutation({
    mutationKey: ['mark-summary-read'],
    mutationFn: async (summaryId: string) => {
      const resp = await patch(`summaries/${summaryId}`, JSON.stringify({ read: true }), {
        version: 'v1',
        headers: { 'content-type': 'application/json' },
      })
      if (!resp.ok) {
        throw new Error(`Failed to mark summary as read: ${resp.status}, ${resp.statusText}`)
      }
      const json = await resp.json()
      if (!json.success) {
        throw new Error('Unexpected error marking summary as read')
      }
      return json.data
    },
    onMutate: async (summaryId) => {
      return updateSummaryOptimistically(summaryId, { read: true })
    },
    onError: (err, summaryId, context) => {
      rollbackSummary(summaryId, context)
      console.error('Failed to mark summary as read:', err)
    },
    onSuccess: (_data, summaryId) => {
      invalidateSummaryQueries(summaryId)
    },
  })
}

export function useEditSummary() {
  const { patch } = useApi()
  const { updateSummaryOptimistically, rollbackSummary, invalidateSummaryQueries } = useSummaryCache()

  return useMutation({
    mutationKey: ['edit-summary'],
    mutationFn: async ({ summaryId, summary }: { summaryId: string; summary: string }) => {
      const resp = await patch(`summaries/${summaryId}`, JSON.stringify({ summary }), {
        version: 'v1',
        headers: { 'content-type': 'application/json' },
      })
      if (!resp.ok) {
        throw new Error(`Failed to edit summary: ${resp.status}, ${resp.statusText}`)
      }

      const json = await resp.json()
      if (!json.success) {
        throw new Error('Unexpected error editing summary')
      }

      return json.data
    },
    onMutate: async ({ summaryId, summary }) => {
      return updateSummaryOptimistically(summaryId, { summary })
    },
    onError: (err, { summaryId }, context) => {
      rollbackSummary(summaryId, context)
      console.error('Failed to edit summary:', err)
    },
    onSuccess: (_data, { summaryId }) => {
      invalidateSummaryQueries(summaryId)
    },
  })
}

export function useSubmitSummaryFeedback() {
  const { patch } = useApi()
  const { updateSummaryOptimistically, rollbackSummary, invalidateSummaryQueries } = useSummaryCache()

  return useMutation({
    mutationKey: ['submit-summary-feedback'],
    mutationFn: async ({ summaryId, feedback }: { summaryId: string; feedback: string }) => {
      const resp = await patch(`summaries/${summaryId}`, JSON.stringify({ feedback }), {
        version: 'v1',
        headers: { 'content-type': 'application/json' },
      })
      if (!resp.ok) {
        throw new Error(`Failed to leave summary feedback: ${resp.status}, ${resp.statusText}`)
      }

      const json = await resp.json()
      if (!json.success) {
        throw new Error('Unexpected error leaving summary feedback')
      }
      return json.data
    },
    onMutate: async ({ summaryId, feedback }) => {
      return updateSummaryOptimistically(summaryId, { feedback })
    },
    onError: (err, { summaryId }, context) => {
      rollbackSummary(summaryId, context)
      console.error('Failed to submit summary feedback:', err)
    },
    onSuccess: (_data, { summaryId }) => {
      invalidateSummaryQueries(summaryId)
    },
  })
}
